import * as React from 'react'
import {
  getPreBuiltInDashboards,
  TimeRange
} from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/PreBuilt'
import { AutoRefreshFrequency } from '@mulesoft/monitoring-center-components/lib/components/Toolbar/AutoRefresh/model'
import { DashboardError, DashboardLoadingState, IframeLoadingState } from '@mulesoft/monitoring-center-components'
import { EnvironmentSelectorState } from '../../common/environment/reducer'
import { getAPIAnalyticsDashboards } from './APIAnalytics/dashboards'
import { ResourceSelectorState } from '../../common/resource/reducer'
import { VersionSelectorState } from '../../common/version/reducer'
import { DetailedResource } from '../../common/resource/model'
import { getCustomMetricsProps } from './CustomMetrics/model'
import { Timezone } from '../../services/VisualizerService'
import {
  dashboardRegistry,
  getFirstApiAnalyticsDashboardType,
  getFirstApiMetricDashboardType,
  isDashboardNotSupportedForApiAnalyticsTypes,
  isDashboardNotSupportedForApiMetricTypes,
  selectFree
} from './dashboardType'
import { FeaturesCompatibility } from '../../common/compatibility/model'
import { getBuiltInURL } from '../Sidebar/Urls'
import { supportsRuntimeVersion } from '@mulesoft/monitoring-center-components/lib/components/SupportedRuntimesHelper/SupportedRuntimesHelper'
import { MonitoringLocalStorage } from '../../util/storage/MonitoringLocalStorageManager'
import { Profile } from '../../remote-api/CoreServicesRemoteApi'
import CustomMetricsContainer from './CustomMetrics/CustomMetricsContainer'

import {
  getResourceMuleVersion,
  isAPIDetail,
  isCloudHubApp,
  isManagedAPI,
  isTarget,
  Resource
} from '@mulesoft/monitoring-center-components/lib/models/resource'

import { DashboardItem } from
    '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/BuiltInDashboardsContent/BuiltInDashboardsContent'
import { buildApplicationNetworkProps } from './ApplicationNetwork/model'
import { ApplicationNetwork } from './ApplicationNetwork/ApplicationNetwork'
import { isPCEOrGov } from '../../util/environments'
import { isFlex } from '../../util/flex';
import FeatureFlagService from '../../services/FeatureFlagService';
import FeatureFlags from '../../types/featureflags';
import { shouldShowEnhancedViewOfAnalyticsDashboard } from './common/utils';

export type BuiltInLandingPageState = {
  environmentSelector: EnvironmentSelectorState
  resourceSelector: ResourceSelectorState
  versionSelector: VersionSelectorState
}

export type DetailedResourceState = {
  isFetching: boolean
  detailedResource?: DetailedResource
  hidden?: boolean
}

export type BuiltInState = {
  landingPage: BuiltInLandingPageState
  environmentSelector: EnvironmentSelectorState
  resourceSelector: ResourceSelectorState
  versionSelector: VersionSelectorState
  isLoading: IframeLoadingState
  isLogPointsCompatible?: boolean
  details: DetailedResourceState
  range?: TimeRange
  autoRefresh: {
    checked?: boolean,
    frequency?: AutoRefreshFrequency
  }
}

export const builtInTimezone = Timezone.Browser
export const builtInOrigin: keyof MonitoringLocalStorage = 'builtIn'
export const builtInLPOrigin = 'builtInLandingPage'
export const builtInOriginOptions = {origin: builtInOrigin, lsOrigin: builtInOrigin}
export const builtInLPOriginOptions = {origin: builtInLPOrigin, lsOrigin: builtInOrigin}

const checkAPIMetricsDashboards = (environmentId: string, dashboardType: string, resourceId: string, isAPIMetrics: boolean, versionId?: string) => {
  const splittedURL = window.location.hash.split('/')
  if (splittedURL[3] === 'api') {
    const urlDashboardType = splittedURL[splittedURL.length - 1]
    if (isAPIMetrics && isDashboardNotSupportedForApiMetricTypes(urlDashboardType)) {
      window.location.hash = getBuiltInURL(environmentId, getFirstApiMetricDashboardType(), resourceId, 'api', undefined, versionId)
    } else if (!isAPIMetrics && isDashboardNotSupportedForApiAnalyticsTypes(urlDashboardType)) {
      window.location.hash = getBuiltInURL(environmentId, getFirstApiAnalyticsDashboardType(), resourceId, 'api', undefined, versionId)
    }
  }
}

function buildApplicationNetworkDashboard(resource: Resource, range: TimeRange,
                                          profile: Profile | undefined,
                                          onDashboardStateChange: (loading: DashboardLoadingState, uri: string, iframe?: HTMLIFrameElement) => void,
                                          onDashboardError: (error: DashboardError) => void) {
  return {
    item: dashboardRegistry.applicationNetwork,
    get: () => <ApplicationNetwork {...buildApplicationNetworkProps(resource, range, profile, onDashboardStateChange, onDashboardError)}/>
  }
}

function buildCustomMetricsDashboard(resource: Resource,
                                     range: TimeRange,
                                     onDashboardFunctions: {
                                       onDashboardStateChange: (loading: DashboardLoadingState, uri: string, iframe?: HTMLIFrameElement) => void;
                                       onDashboardError: (error: DashboardError) => void
                                     },
                                     isTitanium: boolean,
                                     dashboardType: string) {
  const customMetricsProps = getCustomMetricsProps(resource, range, onDashboardFunctions, isTitanium, dashboardType)
  return {
    item: dashboardRegistry.customMetrics,
    get: () => <CustomMetricsContainer {...customMetricsProps}/>
  }
}

export function getDashboards(profile: Profile | undefined,
                              resource: Resource,
                              range: TimeRange,
                              onDashboardFunctions: {
                                onDashboardStateChange: (loading: DashboardLoadingState, uri: string, iframe?: HTMLIFrameElement) => void
                                onDashboardError: (error: DashboardError) => void
                              },
                              isTitanium: boolean,
                              dashboardType: string,
                              environmentId: string,
                              featuresCompatibility?: FeaturesCompatibility,
                              versionId?: string,
                              apiMetricsView?: boolean): DashboardItem[] {
  let dashboards = getPreBuiltInDashboards(profile, resource, range, onDashboardFunctions, isTitanium, undefined, true)
  const isApiAnalyticsBuiltInDashboardDisabled = FeatureFlagService.get(FeatureFlags.API_ANALYTICS_BUILT_IN_DASHBOARD_DISABLED)
  const isHideIncorrectChartsInBuiltInDashboardsEnabled = FeatureFlagService.get(
    FeatureFlags.HIDE_INCORRECT_CHARTS_IN_BUILT_IN_DASHBOARDS_ENABLED,
  );
  if (profile) {
    profile.featureFlags = {
      [FeatureFlags.HIDE_INCORRECT_CHARTS_IN_BUILT_IN_DASHBOARDS_ENABLED]: isHideIncorrectChartsInBuiltInDashboardsEnabled,
    };
  }
  if (isManagedAPI(resource)) {
    dashboards = getAPIAnalyticsDashboards(profile, resource, range, onDashboardFunctions, isTitanium)
  } else if (isTarget(resource)) {
    return dashboards
  } else if (!isAPIDetail(resource)) {
    dashboards.push(buildCustomMetricsDashboard(resource, range, onDashboardFunctions, isTitanium, dashboardType))
    dashboards.push(buildApplicationNetworkDashboard(resource, range, profile,
                                                     onDashboardFunctions.onDashboardStateChange, onDashboardFunctions.onDashboardError));
  } else if (featuresCompatibility || isPCEOrGov() || isFlex(resource)) {
    const isHTTPMetricsEnabled = window.mc.featureFlags.builtIn.apiMetrics.enabled === 'true'
    /*
    featuresCompatibility: Checks if there are metrics in rp_general.api_summary_metric
    https://github.com/mulesoft/monitoring-center-ui/blob/722569bfd1cd7714aa33c26bb7b2c14854993334/src/common/compatibility/actions.ts#L20
     */
    const isHTTPMetricsCompatible = featuresCompatibility && featuresCompatibility.apiMetrics.compatible

    // If API_ANALYTICS_BUILT_IN_DASHBOARD_DISABLED disabled feature flag is true, we must not show the metrics from the api-analytics dashboard.
    if (shouldShowEnhancedViewOfAnalyticsDashboard(isApiAnalyticsBuiltInDashboardDisabled, resource, isHTTPMetricsEnabled, isHTTPMetricsCompatible, apiMetricsView)) {
      dashboards = getAPIAnalyticsDashboards(profile, resource, range, onDashboardFunctions, isTitanium)
      checkAPIMetricsDashboards(environmentId, dashboardType, resource.apiId, true, versionId)
    } else {
      checkAPIMetricsDashboards(environmentId, dashboardType, resource.apiId, false, versionId)
    }
  }

  // filter if not titanium or  non supported runtime version for CH app.
  if (!isTitanium || (isCloudHubApp(resource) && !supportsRuntimeVersion(getResourceMuleVersion(resource)))) {
    return selectFree(dashboards)
  }
  return dashboards
}
